import React from 'react'

import { getMqtt, GetMQTTResponse } from '@/api/mqtt.api'

type MeasurementsSnapshot = {
  key: string
  data: GetMQTTResponse[]
}

const useDeviceMeasurementsHistory = (
  deviceId: string | undefined,
  selectedTemplateId: string | undefined,
  enabled: boolean,
) => {
  const [measurementsSnapshot, setMeasurementsSnapshot] = React.useState<MeasurementsSnapshot | null>(null)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<Error | null>(null)
  const fetchedKeyRef = React.useRef<string | null>(null)
  const requestedKey = enabled && deviceId && selectedTemplateId ? `${deviceId}:${selectedTemplateId}` : null
  const deviceMeasurementsHistory = measurementsSnapshot?.key === requestedKey ? measurementsSnapshot.data : null

  React.useEffect(() => {
    if (!requestedKey || !deviceId || !selectedTemplateId) return

    const key = requestedKey
    if (measurementsSnapshot?.key === key) {
      fetchedKeyRef.current = key
      return
    }
    if (fetchedKeyRef.current === key) return

    let cancelled = false
    fetchedKeyRef.current = key

    const fetchMqttData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const mqttData = await getMqtt({ id: deviceId, selectedTemplateId })

        if (!cancelled) setMeasurementsSnapshot({ key, data: mqttData })
      } catch (err) {
        if (cancelled) return

        fetchedKeyRef.current = null
        setError(err instanceof Error ? err : new Error('Failed to fetch MQTT data'))
        setMeasurementsSnapshot((current) => (current?.key === key ? null : current))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void fetchMqttData()

    return () => {
      cancelled = true
      if (fetchedKeyRef.current === key) fetchedKeyRef.current = null
    }
  }, [deviceId, measurementsSnapshot, requestedKey, selectedTemplateId])

  return { deviceMeasurementsHistory, isLoading, error }
}

export default useDeviceMeasurementsHistory
